import { useCallback, useEffect, useRef, useState } from "react";

import type { SegmentBudget } from "../services/segmentBudget";
import { resolveSegmentBudget, UNCAPPED_SEGMENT_BUDGET } from "../services/segmentBudget";

type UseSegmentBudgetReturn = SegmentBudget & {
    isLoading: boolean;
    /** The resolved budget, awaiting the in-flight claims lookup when it has not landed yet. */
    requestBudget: () => Promise<SegmentBudget>;
};

/** Resolved once per builder visit: the account's claims do not change mid-run. */
export const useSegmentBudget = (): UseSegmentBudgetReturn => {
    const [budget, setBudget] = useState<SegmentBudget | null>(null);
    const budgetPromiseRef = useRef<Promise<SegmentBudget> | null>(null);

    // Kept as a promise, not just as state: a user who submits before the lookup lands must
    // still get a run capped to what the account has, never an uncapped one.
    const requestBudget = useCallback((): Promise<SegmentBudget> => {
        budgetPromiseRef.current ??= resolveSegmentBudget();

        return budgetPromiseRef.current;
    }, []);

    useEffect(() => {
        let isCurrent = true;

        void requestBudget().then((resolved) => {
            if (isCurrent) {
                setBudget(resolved);
            }
        });

        return () => {
            isCurrent = false;
        };
    }, [requestBudget]);

    // Until it resolves, nothing is warned about on a guess — the compose screen only claims
    // the account is out of segments once that is actually known.
    return {
        maxSegments: budget?.maxSegments ?? UNCAPPED_SEGMENT_BUDGET,
        hasSegmentsModule: budget?.hasSegmentsModule ?? true,
        isLoading: !budget,
        requestBudget,
    };
};
